<template>
  <b-overlay :show="formShow" rounded="sm" no-fade>
    <b-card class="m-0 p-0">
      <b-row class="mb-2">
        <b-col cols="12" class="mb-1 mb-md-0">
          <div class="date-container d-flex flex-column">
            <b-row class="d-flex align-items-center justify-content-between">
              <b-col cols="12" md="4" class="d-flex align-items-center">
                <h4 class="mb-0">Working Group Tool</h4>
              </b-col>
              <b-col
                cols="12"
                md="8"
                class="d-flex align-items-center justify-content-end"
                v-if="$Can('working_tool_filter_options')"
              >
                <b-input-group>
                  <flat-pickr
                    v-model="filter_wgtool_date"
                    class="form-control"
                    :config="wgtool_date_config"
                    @on-change="filterWgtoolDate"
                    placeholder="Filter by Working Group Tool Date"
                  />
                  <b-input-group-append>
                    <b-button
                      @click="clearWgtoolDate"
                      variant="primary"
                      size="sm"
                    >
                      Clear
                    </b-button>
                  </b-input-group-append>
                </b-input-group>

                <b-form-input
                  v-model="filter"
                  class="d-inline-block ml-1"
                  placeholder="Search in table..."
                />

                <b-button
                  variant="outline-success"
                  @click="downloadReport"
                  style="margin-left: 5px; white-space: nowrap;"
                >
                  <span class="mr-1">Working Group Tool Report</span>
                  <i class="fas fa-file-excel"></i>
                </b-button>
              </b-col>
            </b-row>
          </div>
        </b-col>
      </b-row>

      <div v-if="$Can('show_working_tool_table')">
        <b-table
          sticky-header
          striped
          hover
          bordered
          ref="refActionListTable"
          :outlined="true"
          :fields="workingGroupFields"
          :items="filteredWorkingGroups"
          responsive="sm"
          :small="true"
          class="table table-sm table-responsive"
          :filter="filter"
          @filtered="onFiltered"
          :filter-included-fields="filterOn"
          show-empty
          empty-text="No matching records found"
          :current-page="currentPage"
          :per-page="perPage"
          @row-clicked="openSubsectionInputsModal"
        >
          <template #cell(group_name)="data">
            <div style="font-size: 1rem;" class="p-25">
              <p>{{ data.value }}</p>
            </div>
          </template>

          <template #cell(terms_of_reference)="data">
            <div style="font-size: 1rem;" class="p-25">
              <span :id="`terms-${data.index}`" class="terms-reference-text">
                {{
                  data.value
                    ? data.value.length > 180
                      ? data.value.substring(0, 180) + "..."
                      : data.value
                    : "-"
                }}
              </span>
              <b-popover
                v-if="data.value && data.value.length > 180"
                :target="`terms-${data.index}`"
                triggers="hover"
                placement="auto"
                custom-class="terms-popover"
                boundary="viewport"
              >
                <template #title>Terms of Reference</template>
                <div class="terms-popover-content">{{ data.value }}</div>
              </b-popover>
            </div>
          </template>

          <template #cell(document)="data">
            <div class="d-flex justify-content-center align-items-center">
              <b-button
                variant="outline-info"
                size="sm"
                class="mr-1"
                @click="showDocumentsModal(data.item)"
                v-b-tooltip.hover="{ delay: { show: 1000, hide: 500 } }"
                title="View All Documents"
              >
                <i class="fas fa-folder-open"></i>
              </b-button>
            </div>
          </template>

          <template #cell(actions)="data">
            <div>
              <b-button
                v-if="$Can('show_subsection_inputs') && getUpdateRights(data.item)"
                variant="outline-primary"
                size="sm"
                @click="openSubsectionInputsModal(data.item)"
                class="action-row-btn"
              >
                <span class="trades-font">
                  {{ "Update" }}
                  <feather-icon icon="EditIcon" style="margin-left: 0.3rem;" />
                </span>
              </b-button>

              <b-button
                v-if="$Can('show_subsection_report')"
                :disabled="data.item.subsections_count === 0"
                variant="outline-info"
                size="sm"
                @click="dowloadIndividualSubsectionReport(data.item)"
                class="action-row-btn"
                style="margin-left: 5px; white-space: nowrap;"
              >
                <span class="trades-font">
                  {{ "Report" }}
                  <feather-icon icon="FileIcon" />
                </span>
              </b-button>
            </div>
          </template>

          <template #cell(updates)="data">
            <div>
              <feather-icon
                v-b-tooltip.hover
                :id="`recent-popover-${data.index}`"
                icon="ClockIcon"
                class="ml-auto text-primary"
                size="25"
                :badge="data.item.recent_subsections_count"
                badge-classes="bg-danger"
              />

              <b-popover
                :target="`recent-popover-${data.index}`"
                triggers="hover focus"
                placement="auto"
                custom-class="subsections-popover"
                @shown="markUpdatesRead(data.item)"
              >
                <!-- Popover Title -->
                <template #title>
                  Updates ({{ data.item.subsections.length }})
                </template>

                <!-- If no subsections, show a placeholder -->
                <div v-if="data.item.subsections.length === 0">
                  <em>No updates</em>
                </div>

                <!-- Otherwise, render a small table -->
                <div v-else class="table-responsive">
                  <table class="table table-sm mb-0">
                    <thead>
                      <tr>
                        <th style="width:10%">Recent</th>
                        <th style="width:90%">Title</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="sub in data.item.subsections"
                        :key="sub.id"
                        :class="{ 'table-danger': sub.recent }"
                      >
                        <td class="text-center" style="width:10%">
                          <!-- show a little clock icon if it’s recent -->
                          <b-badge variant="info" pill v-if="sub.recent"
                            >Recent</b-badge
                          >
                        </td>
                        <!-- If you want just the title_and_comment in one column,
                   you could omit the second <td> below and merge them. -->
                        <td style="width:90%">
                          <span class="font-weight-bold">
                            {{ sub.meeting_title }}
                          </span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </b-popover>
            </div>
          </template>
        </b-table>

        <!-- Add pagination below table -->
        <div class="mx-2 mb-1 mt-1">
          <b-row>
            <b-col
              cols="12"
              sm="6"
              class="d-flex align-items-center justify-content-center justify-content-sm-start"
            >
              <span class="text-muted"
                >Showing {{ dataMeta.from }} to {{ dataMeta.to }} of
                {{ dataMeta.of }} entries</span
              >
            </b-col>

            <!-- Pagination -->
            <b-col
              cols="12"
              sm="6"
              class="d-flex align-items-center justify-content-center justify-content-sm-end"
            >
              <b-pagination
                v-model="currentPage"
                :total-rows="totalRows"
                :per-page="perPage"
                first-number
                last-number
                class="mb-0 mt-1 mt-sm-0"
                prev-class="prev-item"
                next-class="next-item"
              >
                <template #prev-text>
                  <feather-icon icon="ChevronLeftIcon" size="18" />
                </template>
                <template #next-text>
                  <feather-icon icon="ChevronRightIcon" size="18" />
                </template>
              </b-pagination>
            </b-col>
          </b-row>
        </div>
      </div>

      <b-alert variant="primary" show v-else>
        <div class="alert-body">
          <span
            ><strong style="display: block;"
              >You are not authorized to view this tool</strong
            ></span
          >
        </div>
      </b-alert>
    </b-card>

    <!-- update inputs modal -->
    <b-modal
      id="subsection-inputs-modal"
      title="Update Inputs"
      centered
      size="xl"
      no-close-on-backdrop
      no-close-on-esc
      @hidden="resetSubsectionInputsModal"
    >
      <b-overlay :show="subSectionFormShow" rounded="sm" no-fade>
        <validation-observer ref="subsectionInputRules">
          <b-form
            v-if="
              $Can('show_subsection_add_button') ||
                $Can('show_subsection_edit_button')
            "
          >
            <b-row v-if="formattedActions.length">
              <b-col cols="12">
                <app-collapse accordion :type="'border'" class="mb-2">
                  <app-collapse-item title="Connected Actions">
                    <div v-if="formattedActions.length">
                      <div
                        v-for="(action, index) in formattedActions"
                        :key="index"
                        class="border-left mb-2 pl-2"
                        :style="{
                          borderLeftColor: action.pillarColor + ' !important',
                        }"
                      >
                        <div
                          class="font-weight-bold"
                          :style="{ color: action.pillarColor }"
                        >
                          {{ action.displayText.pillarTitle || "-" }}
                        </div>
                        <div class="small text-muted">
                          {{ action.displayText.remainingText }}
                        </div>
                      </div>
                    </div>
                    <div v-else class="text-muted text-center py-2">
                      No connected actions available
                    </div>
                  </app-collapse-item>
                </app-collapse>
              </b-col>
            </b-row>

            <b-row>
              <b-col cols="12" md="6">
                <!-- Meeting Title -->
                <b-form-group label="Title">
                  <validation-provider
                    name="title"
                    #default="{ errors }"
                    rules="required"
                  >
                    <b-form-textarea
                      v-model="subsectionInputsForm.meeting_title"
                      placeholder="Enter title"
                      rows="2"
                      style="resize: none;"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>

              <b-col cols="12" md="6">
                <!-- Additional Comments -->
                <b-form-group label="Comments">
                  <b-form-textarea
                    v-model="subsectionInputsForm.additional_comment"
                    placeholder="Enter comments"
                    rows="2"
                    style="resize: none;"
                  />
                </b-form-group>
              </b-col>
            </b-row>

            <!-- Dates -->
            <b-row>
              <b-col cols="12" md="6">
                <b-row>
                  <b-col cols="12" md="6">
                    <b-form-group label="Date">
                      <validation-provider
                        name="date"
                        #default="{ errors }"
                        rules="required"
                      >
                        <flat-pickr
                          v-model="subsectionInputsForm.commence_date"
                          class="form-control"
                          :config="flatPickrConfig"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>
                    </b-form-group>
                  </b-col>
                </b-row>
              </b-col>

              <b-col cols="12" md="6">
                <!-- File Upload -->
                <b-form-group label="File">
                  <b-form-file
                    v-model="subsectionInputsForm.meeting_minutes_file"
                    placeholder="Choose a PDF file or drop it here..."
                    drop-placeholder="Drop PDF file here..."
                    accept=".pdf,application/pdf"
                    :state="
                      validateFileType(
                        subsectionInputsForm.meeting_minutes_file
                      )
                    "
                  />
                </b-form-group>
              </b-col>
            </b-row>
            <hr />
            <b-row class="mt-2">
              <b-col cols="12" class="text-right d-flex justify-content-end">
                <b-button
                  variant="primary"
                  size="sm"
                  class="d-flex align-items-center"
                  @click="handleSubsectionInputsSubmit"
                  v-if="
                    $Can('show_subsection_add_button') ||
                      $Can('show_subsection_edit_button')
                  "
                >
                  <span>{{ isEditMode ? "Update" : "Add New Update" }}</span>
                  <feather-icon
                    :icon="isEditMode ? 'Edit2Icon' : 'PlusIcon'"
                    size="16"
                    class="ml-1"
                  />
                </b-button>
              </b-col>
            </b-row>
          </b-form>
        </validation-observer>

        <!-- Grouped Subsections Table -->
        <div v-if="workingGroupActions.length > 0">
          <b-table
            :items="workingGroupActions"
            :fields="subsectionFields"
            responsive
            striped
            class="mt-2"
            show-empty
            empty-text="No matching records found"
            small
          >
            <!-- Action Info Column -->

            <!-- Existing cell templates... -->
            <template #cell(meeting_title)="data">
              <div class="truncate-text" :id="`meeting-${data.index}`">
                {{ data.item.meeting_title ? data.item.meeting_title : "-" }}
              </div>
              <b-popover
                v-if="data.item.meeting_title"
                :target="`meeting-${data.index}`"
                triggers="hover"
                placement="auto"
                boundary="viewport"
                delay="500"
              >
                <template #title>Title</template>
                <div class="popover-content">
                  {{ data.item.meeting_title }}
                </div>
              </b-popover>
            </template>

            <template #cell(additional_comment)="data">
              <div class="truncate-text" :id="`comment-${data.index}`">
                {{
                  data.item.additional_comment
                    ? data.item.additional_comment
                    : "-"
                }}
              </div>
              <b-popover
                v-if="data.item.additional_comment"
                :target="`comment-${data.index}`"
                triggers="hover"
                placement="auto"
                boundary="viewport"
                delay="500"
              >
                <template #title>Comments</template>
                <div class="popover-content">
                  {{ data.item.additional_comment }}
                </div>
              </b-popover>
            </template>

            <template #cell(commence_date)="data">
              {{
                data.item.commence_date
                  ? formatDate(data.item.commence_date)
                  : "-"
              }}
            </template>

            <template #cell(actions)="data">
              <div class="d-flex justify-content-start align-items-center">
                <b-button
                  variant="outline-warning"
                  size="sm"
                  class="mr-1"
                  @click="editSubsection(data.item)"
                  v-if="$Can('show_subsection_edit_button')"
                >
                  <feather-icon icon="EditIcon" size="16" />
                  Edit
                </b-button>

                <b-button
                  variant="outline-danger"
                  size="sm"
                  class="mr-1"
                  @click="deleteSubsection(data.item.id)"
                  v-if="$Can('show_subsection_delete_button')"
                >
                  <feather-icon icon="TrashIcon" size="16" />
                  Delete
                </b-button>

                <b-button
                  v-if="data.item.meeting_minutes_file"
                  variant="outline-primary"
                  size="sm"
                  @click="
                    downloadFile(
                      data.item.id,
                      data.item.meeting_minutes_file_name
                    )
                  "
                >
                  <feather-icon icon="DownloadIcon" size="16" />
                  Download
                </b-button>
              </div>
            </template>
          </b-table>
        </div>

        <b-alert
          class="mt-2"
          variant="info"
          show
          v-if="workingGroupActions.length === 0"
        >
          <h4 class="alert-heading">No updates found</h4>
          <div class="alert-body">
            <span>This group has no updates</span>
          </div>
        </b-alert>
      </b-overlay>

      <!-- Custom footer -->
      <template #modal-footer>
        <b-button
          variant="secondary"
          @click="$bvModal.hide('subsection-inputs-modal')"
        >
          Close
        </b-button>
      </template>
    </b-modal>

    <!-- Add this modal component near the end of the template, before the closing </b-overlay> -->
    <b-modal
      id="documents-modal"
      title="Available Documents"
      centered
      size="lg"
      hide-footer
    >
      <b-list-group>
        <!-- Main document -->
        <b-list-group-item
          v-if="workingGroupDocuments.document_file.attached_document_name"
          class="d-flex justify-content-between align-items-center"
        >
          <div>
            <strong>Document:</strong>
            {{ workingGroupDocuments.document_file.attached_document_name }}
          </div>
          <b-button
            variant="info"
            size="sm"
            @click="
              downloadAttachedDocument(
                workingGroupDocuments.document_file.id,
                workingGroupDocuments.document_file.attached_document_name
              )
            "
          >
            <feather-icon icon="DownloadIcon" size="16" />
            Download
          </b-button>
        </b-list-group-item>
        <b-list-group-item v-else>
          <span class="text-muted">No attached document found</span>
        </b-list-group-item>
        <!-- Meeting Minutes -->
        <div
          class="mt-2"
          v-if="workingGroupDocuments.meeting_minutes_files.length > 0"
        >
          <b-list-group-item
            v-for="(doc, index) in workingGroupDocuments.meeting_minutes_files"
            :key="doc.id"
            class="d-flex justify-content-between align-items-center"
          >
            <div>
              <div class="truncate-text" style="max-width: 400px;">
                <strong>{{ index + 1 }}. </strong>
                {{ doc.meeting_title }}
              </div>

              <small class="d-block text-muted">
                Date: {{ formatDate(doc.commence_date) }}
              </small>
            </div>
            <b-button
              variant="outline-primary"
              size="sm"
              @click="downloadFile(doc.id, doc.meeting_minutes_file_name)"
            >
              <feather-icon icon="DownloadIcon" size="16" />
              Download
            </b-button>
          </b-list-group-item>
        </div>
        <b-list-group-item
          v-if="!hasAnyDocuments"
          class="text-center text-muted"
        >
          No documents available
        </b-list-group-item>
      </b-list-group>
    </b-modal>
  </b-overlay>
</template>

<script>
import { BCard, BRow, BCol, BButton, BOverlay, VBPopover } from "bootstrap-vue";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import store from "@/store";
import { onUnmounted } from "@vue/composition-api";
import vSelect from "vue-select";
import VueContext from "vue-context";
import toolsStoreModule from "./toolStoreModule";
import flatPickr from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import axiosIns from "@axios";
import { required, alphaNum, email } from "@validations";
import AppCollapse from "@core/components/app-collapse/AppCollapse.vue";
import AppCollapseItem from "@core/components/app-collapse/AppCollapseItem.vue";

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BButton,
    BOverlay,
    vSelect,
    ToastificationContent,
    VueContext,
    flatPickr,
    ValidationProvider,
    ValidationObserver,
    AppCollapse,
    AppCollapseItem,
  },
  directives: {
    "b-popover": VBPopover,
  },

  setup() {
    const TOOL_APP_STORE_MODULE_NAME = "tools";
    if (!store.hasModule(TOOL_APP_STORE_MODULE_NAME))
      store.registerModule(TOOL_APP_STORE_MODULE_NAME, toolsStoreModule);

    onUnmounted(() => {
      if (store.hasModule(TOOL_APP_STORE_MODULE_NAME))
        store.unregisterModule(TOOL_APP_STORE_MODULE_NAME);
    });
  },

  data() {
    return {
      dateNow: new Date().toLocaleDateString(),
      formShow: false,
      subSectionFormShow: false,
      filter_wgtool_date: null,

      userData: JSON.parse(localStorage.getItem("userData") || "{}"),
      workingGroups: [],
      filter: null,
      filterOn: ["group_name", "terms_of_reference"],
      currentPage: 1,
      perPage: localStorage.getItem("workingGroupShow") ?? 100,
      totalRows: 0,
      perPageOptions: [10, 25, 50, 100],

      workingGroupFields: [
        {
          key: "updates",
          sortable: false,
          class: "text-center",
          thStyle: "width:5%;color:white!important",
        },

        {
          key: "group_name",
          sortable: true,
          class: "text-left",
          thStyle: "width:30%;color:white!important",
        },

        {
          key: "terms_of_reference",
          sortable: true,
          class: "text-left",
          thStyle: "width:40%;color:white!important",
        },
        {
          key: "document",
          sortable: false,
          class: "text-center",
          thStyle: "width:10%;color:white!important",
        },
        {
          key: "actions",
          sortable: false,
          class: "text-center",
          thStyle: "width:15%;color:white!important",
        },
      ],

      selectedRowData: null,
      selectedIndices: null,
      selectedAction: null,

      subsectionInputsForm: {
        id: null,
        wg_id: null,
        action_tool_id: null,
        meeting_title: null,
        commence_date: null,
        meeting_minutes_file: null,
        additional_comment: null,
      },
      selectedRowId: null,
      wgtool_date_config: {
        mode: "range",
        enableTime: false,
        dateFormat: "Y-m-d",
        altInput: true,
        altFormat: "F j, Y",
        allowInput: true,
        clear: true,
        plugins: [
          ShortcutButtonsPlugin({
            button: [
              {
                label: "This Month",
                icon: "fa-solid fa-calendar-month",
              },
              {
                label: "Next Month",
                icon: "fa-solid fa-calendar-month",
              },
              {
                label: "This Year",
                icon: "fa-solid fa-calendar-month",
              },
            ],
            label: "or",
            onClick: (index, fp) => {
              let startDate, endDate;
              const today = new Date();

              switch (index) {
                case 0:
                  startDate = new Date(
                    today.getFullYear(),
                    today.getMonth(),
                    1
                  );
                  endDate = new Date(
                    today.getFullYear(),
                    today.getMonth() + 1,
                    0
                  );
                  this.filter_wgtool_date = `${this.formatDateYMD(
                    startDate
                  )} to ${this.formatDateYMD(endDate)}`;
                  this.getWorkingGroupTools();
                  break;
                case 1:
                  startDate = new Date(
                    today.getFullYear(),
                    today.getMonth() + 1,
                    1
                  );
                  endDate = new Date(
                    today.getFullYear(),
                    today.getMonth() + 2,
                    0
                  );
                  this.filter_wgtool_date = `${this.formatDateYMD(
                    startDate
                  )} to ${this.formatDateYMD(endDate)}`;
                  this.getWorkingGroupTools();
                  break;
                case 2:
                  startDate = new Date(today.getFullYear(), 0, 1);
                  endDate = new Date(today.getFullYear(), 11, 0);
                  this.filter_wgtool_date = `${this.formatDateYMD(
                    startDate
                  )} to ${this.formatDateYMD(endDate)}`;
                  this.getWorkingGroupTools();
                  break;
              }

              fp.setDate([startDate, endDate]);
            },
          }),
        ],
      },
      flatPickrConfig: {
        enableTime: false,
        dateFormat: "Y-m-d",
        altInput: true,
        altFormat: "F j, Y",
      },
      subsections: [],
      subsection_actions: [],
      subsectionFields: [
        {
          key: "meeting_title",
          label: "Title",
          thStyle: { width: "25%" },
        },
        {
          key: "additional_comment",
          label: "Comments",
          thStyle: { width: "25%" },
        },
        {
          key: "commence_date",
          label: "Date",
          thStyle: { width: "10%" },
        },
        {
          key: "actions",
          label: "Actions",
          thStyle: { width: "20%" },
        },
      ],
      isEditMode: false,
      workingGroupActions: [],
      wg_id: null,
      workingGroupDocuments: {
        document_file: {
          attached_document_name: null,
          attached_documents: null,
        },
        meeting_minutes_files: [],
      },
    };
  },

  mounted() {
    this.getWorkingGroupTools();
  },

  computed: {
    formattedActions() {
      return this.subsection_actions.map((action) => ({
        action_tool_id: action.action_tool_id,
        displayText: {
          pillarTitle: action.pillar_title ? action.pillar_title : "-",
          remainingText: ` - ${
            action.pillar_subtitle ? action.pillar_subtitle : "-"
          } | ${action.objective_title ? action.objective_title : "-"} | ${
            action.action_title
              ? this.truncateText(action.action_title, 100)
              : "-"
          }`,
        },
        original: action,
        customStyles: {
          borderLeft: `3px solid ${action.pillar_color || "#007bff"}`,
          paddingLeft: "8px",
          backgroundColor: `${action.pillar_color}15`,
        },
        pillarColor: action.pillar_color || "#2c3e50",
      }));
    },
    combinedSubsections() {
      return this.workingGroupActions.flatMap((action) => {
        return action.subsections.map((subsection) => ({
          ...subsection,
          pillar_title: action.pillar_title,
          pillar_subtitle: action.pillar_subtitle,
          pillar_color: action.pillar_color,
          objective_title: action.objective_title,
          action_title: action.action_title,
          action_tool_id: action.action_tool_id,
        }));
      });
    },
    isAdmin() {
      return this.userData?.name === "Admin";
    },
    defaultCompanyId() {
      return !this.userData?.company_id ? null : this.userData?.company_id;
    },
    meetingTitleState() {
      return this.subsectionInputsForm.meeting_title ? true : false;
    },
    dataMeta() {
      return {
        from:
          this.totalRows != 0 ? this.perPage * (this.currentPage - 1) + 1 : 0,
        to:
          this.perPage * (this.currentPage - 1) + Number(this.perPage) <
          this.totalRows
            ? this.perPage * (this.currentPage - 1) + Number(this.perPage)
            : this.totalRows,
        of: this.totalRows,
      };
    },
    filteredWorkingGroups() {
      let filtered = this.workingGroups;

      if (this.filter) {
        const searchTerm = this.filter.toLowerCase();
        filtered = this.workingGroups.filter(
          (item) =>
            item.group_name.toLowerCase().includes(searchTerm) ||
            (item.terms_of_reference &&
              item.terms_of_reference.toLowerCase().includes(searchTerm))
        );
      }

      return filtered;
    },
    hasAnyDocuments() {
      return (
        this.workingGroupDocuments.document_file ||
        (this.workingGroupDocuments.meeting_minutes_files &&
          this.workingGroupDocuments.meeting_minutes_files.length > 0)
      );
    },
  },

  methods: {
    // Once this user has actually looked at the updates, clear their own badge.
    // Read state is per user, so this never affects what other managers see.
    markUpdatesRead(workingGroup) {
      if (!workingGroup || !workingGroup.id) return;
      if (!workingGroup.recent_subsections_count) return;

      store
        .dispatch("tools/markWorkingGroupUpdatesRead", workingGroup.id)
        .then(() => {
          // reflect it immediately rather than waiting for a reload
          this.$set(workingGroup, "recent_subsections_count", 0);
          (workingGroup.subsections || []).forEach((sub) => {
            this.$set(sub, "recent", false);
          });
        })
        .catch(() => {
          // leaving the badge up is the safe failure: they will see it again
        });
    },
    async getWorkingGroupTools() {
      const params = await this.arrangeWgtoolDate();

      this.formShow = true;
      return store
        .dispatch("tools/getWorkingGroupTools", params)
        .then((res) => {
          this.workingGroups = res.data;
          this.totalRows = res.data.length;
        })
        .catch((error) => {
          console.error("Error fetching working groups:", error);
        })
        .finally(() => {
          this.formShow = false;
        });
    },

    async openSubsectionInputsModal(wg) {
      this.resetSubsectionInputsModal();
      this.wg_id = wg.id;

      try {
        this.formShow = true;
        const response = await store.dispatch(
          "tools/getWorkingGroupToolSubsections",
          wg.id
        );
        this.workingGroupActions = response.data.subsections || [];
        this.subsection_actions = response.data.subsection_actions;
      } catch (error) {
        console.error("Error loading updates:", error);
        this.workingGroupActions = [];
      } finally {
        this.formShow = false;
      }

      this.$bvModal.show("subsection-inputs-modal");
    },

    dowloadIndividualSubsectionReport(wg) {
      this.formShow = true;
      return store
        .dispatch("tools/getWorkingGroupToolIndividualSubsectionReport", wg.id)
        .then((res) => {
          if (res.status === 200) {
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement("a");
            link.href = url;

            let fileName = wg.group_name + ".pdf" ?? "wg_tool_report.pdf";

            const now = new Date();
            fileName = `${wg.group_name}_${now.getFullYear()}-${String(
              now.getMonth() + 1
            ).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}.pdf`;

            link.setAttribute("download", fileName);
            document.body.appendChild(link);
            link.click();

            this.$toast({
              component: ToastificationContent,
              props: {
                title: "Success",
                text: "Report generated successfully",
                icon: "CheckIcon",
                variant: "success",
              },
            });
          } else {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: "Not found Report data",
                text: "Report data not found for this working group.",
                icon: "AlertCircleIcon",
                variant: "warning",
              },
            });
          }
        })
        .catch((error) => {
          console.error("Error fetching Individual Report", error);
        })
        .finally(() => {
          this.formShow = false;
        });
    },

    downloadReport() {
      this.formShow = true;
      return store
        .dispatch("tools/getWorkingGroupToolReport")
        .then((res) => {
          if (res.status === 200) {
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement("a");
            link.href = url;

            let fileName = "working_group_tool_report.pdf";

            const now = new Date();
            fileName = `working_group_tool_report_${now.getFullYear()}-${String(
              now.getMonth() + 1
            ).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}.pdf`;

            link.setAttribute("download", fileName);
            document.body.appendChild(link);
            link.click();

            this.$toast({
              component: ToastificationContent,
              props: {
                title: "Success",
                text: "Report generated successfully",
                icon: "CheckIcon",
                variant: "success",
              },
            });
          } else {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: "Not found Report data",
                text: "Report data not found for these options.",
                icon: "AlertCircleIcon",
                variant: "warning",
              },
            });
          }
        })
        .catch((error) => {
          console.error("Error fetching working groups:", error);
          this.$toast({
            component: ToastificationContent,
            props: {
              title: "Error",
              text: "Failed to generate report",
              icon: "AlertCircleIcon",
              variant: "danger",
            },
          });
        })
        .finally(() => {
          this.formShow = false;
        });
    },

    getUpdateRights(item){
      const users = item.users.map((e) => e.id);
      if(users.includes(this.userData.id) || this.$Can('manage_all_wgs')){
        return true;
      }
        
      return false;


    },

    resetSubsectionInputsModal() {
      this.subsectionInputsForm = {
        id: null,
        wg_id: null,
        action_tool_id: null,
        meeting_title: null,
        commence_date: null,
        meeting_minutes_file: null,
        additional_comment: null,
      };
      this.flatPickrConfig = {
        ...this.flatPickrConfig,
        minDate: null,
        maxDate: null,
      };
      this.selectedAction = null;
      this.isEditMode = false;
      this.getWorkingGroupTools();
    },

    async deleteSubsection(subsectionId) {
      this.$bvModal
        .msgBoxConfirm("Are you sure you want to delete this update?", {
          title: "Please Confirm",
          size: "sm",
          buttonSize: "sm",
          okVariant: "danger",
          okTitle: "Yes",
          cancelTitle: "No",
          footerClass: "p-2",
          hideHeaderClose: false,
          centered: true,
        })
        .then(async (value) => {
          if (value) {
            this.subSectionFormShow = true;
            try {
              await store.dispatch(
                "tools/deleteWorkingGroupToolSubsection",
                subsectionId
              );

              await this.refreshSubsections();

              this.$toast({
                component: ToastificationContent,
                props: {
                  title: "Successfully",
                  text: "✔️ Update deleted successfully",
                  icon: "CheckIcon",
                  variant: "success",
                },
              });
            } catch (error) {
              console.error("Error deleting update:", error);
              this.$toast({
                component: ToastificationContent,
                props: {
                  title: "Error",
                  text: "Failed to delete update",
                  icon: "AlertCircleIcon",
                  variant: "danger",
                },
              });
            } finally {
              this.subSectionFormShow = false;
            }
          }
        });
    },

    async handleSubsectionInputsSubmit() {
      const isValid = await this.$refs.subsectionInputRules.validate();

      if (!isValid) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Error",
            text: "Please fill in all required fields",
            icon: "AlertCircleIcon",
            variant: "warning",
          },
        });

        return;
      }

      // Reject oversized files up-front (must match the server's 50 MB limit)
      // so we don't start a doomed upload that ends in a timeout.
      const MAX_FILE_BYTES = 50 * 1024 * 1024;
      const file = this.subsectionInputsForm.meeting_minutes_file;
      if (file && file.size && file.size > MAX_FILE_BYTES) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "File too large",
            text: `The meeting minutes file must be 50 MB or smaller (selected file is ${(
              file.size /
              (1024 * 1024)
            ).toFixed(1)} MB).`,
            icon: "AlertCircleIcon",
            variant: "warning",
          },
        });
        return;
      }

      try {
        this.subSectionFormShow = true;

        this.subsectionInputsForm.action_tool_id = this.selectedAction ?? null;
        this.subsectionInputsForm.wg_id = await this.wg_id;
        const formData = new FormData();
        Object.keys(this.subsectionInputsForm).forEach((key) => {
          if (this.subsectionInputsForm[key]) {
            formData.append(key, this.subsectionInputsForm[key]);
          }
        });

        if (this.isEditMode) {
          await store.dispatch(
            "tools/updateWorkingGroupToolSubsectionInputs",
            formData
          );
        } else {
          await store.dispatch(
            "tools/createWorkingGroupToolSubsectionInputs",
            formData
          );
        }

        await this.refreshSubsections();

        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Successfully",
            text: `✔️ Update ${
              this.isEditMode ? "updated" : "added"
            } successfully`,
            icon: "CheckIcon",
            variant: "success",
          },
        });

        this.resetSubsectionInputsModal();
      } catch (error) {
        console.error("Error handling update:", error);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Error",
            text: `Failed to ${this.isEditMode ? "update" : "add"}`,
            icon: "AlertCircleIcon",
            variant: "danger",
          },
        });
      } finally {
        this.subSectionFormShow = false;
      }
    },

    editSubsection(subsection) {
      this.isEditMode = true;
      this.selectedAction = subsection.action_tool_id;
      this.subsectionInputsForm = {
        id: subsection.id,
        wg_id: this.wg_id,
        action_tool_id: subsection.action_tool_id,
        meeting_title: subsection.meeting_title,
        commence_date: subsection.commence_date,
        additional_comment: subsection.additional_comment,
        meeting_minutes_file: null,
      };
    },
    async downloadFile(id, meetingMinutesFileName) {
      if (id) {
        return new Promise((resolve, reject) => {
          axiosIns
            .get(`/workingGroup/meetingMinutesFileDownload/${id}`, {
              responseType: "blob",
            })
            .then((response) => {
              const contentType = response.headers["content-type"];
              const url = window.URL.createObjectURL(
                new Blob([response.data], { type: contentType })
              );
              const link = document.createElement("a");
              link.href = url;

              const contentDisposition =
                response.headers["content-disposition"];
              let fileName = meetingMinutesFileName ?? "meeting-minutes";

              if (contentDisposition) {
                const fileNameMatch = contentDisposition.match(
                  /filename="(.+)"/
                );
                if (fileNameMatch && fileNameMatch.length === 2) {
                  fileName = fileNameMatch[1];
                }
              } else {
                switch (contentType) {
                  case "application/pdf":
                    // fileName += ".pdf";
                    break;
                  case "application/vnd.ms-excel":
                    fileName += ".xls";
                    break;
                  case "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
                    fileName += ".xlsx";
                    break;
                  case "application/msword":
                    fileName += ".doc";
                    break;
                  case "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
                    fileName += ".docx";
                    break;
                  default:
                    fileName += ".txt";
                    break;
                }
              }

              link.setAttribute("download", fileName);
              document.body.appendChild(link);
              link.click();
              link.remove();
              window.URL.revokeObjectURL(url);

              resolve(response);
            })
            .catch((error) => {
              console.error("Download failed:", error);
              this.$toast({
                component: ToastificationContent,
                props: {
                  title: "Error",
                  text: "Failed to download file",
                  variant: "danger",
                },
              });
              reject(error);
            });
        });
      }
    },
    async refreshSubsections() {
      try {
        const response = await store.dispatch(
          "tools/getWorkingGroupToolSubsections",
          this.wg_id
        );
        this.workingGroupActions = response.data.subsections || [];
        this.subsection_actions = response.data.subsection_actions;
      } catch (error) {
        console.error("Error refreshing subsections:", error);
      }
    },

    filterWgtoolDate() {
      if (
        this.filter_wgtool_date &&
        this.filter_wgtool_date.split(" to ").length === 2
      ) {
        this.getWorkingGroupTools();
      }
    },
    arrangeWgtoolDate() {
      let params = {};

      if (this.filter_wgtool_date) {
        const currentYear = new Date().getFullYear();
        const [startDate, endDate] = this.filter_wgtool_date.split(" to ");

        if (startDate && endDate) {
          params = {
            start_date: startDate,
            end_date: endDate,
          };
        } else if (startDate && !endDate) {
          params = {
            start_date: startDate,
            end_date: `${currentYear}-12-31`,
          };
        } else if (!startDate && endDate) {
          params = {
            start_date: `${currentYear}-01-01`,
            end_date: endDate,
          };
        } else {
          params = {
            start_date: `${currentYear}-01-01`,
            end_date: `${currentYear}-12-31`,
          };
        }
      } else {
        const currentYear = new Date().getFullYear();
        params = {
          start_date: `${currentYear}-01-01`,
          end_date: `${currentYear}-12-31`,
        };
      }

      return params;
    },

    formatDate(date) {
      if (!date) return "-";
      const dt = new Date(date);
      const day = String(dt.getDate()).padStart(2, "0");
      const month = String(dt.getMonth() + 1).padStart(2, "0");
      const year = dt.getFullYear();
      return `${day}-${month}-${year}`;
    },

    onFiltered(filteredItems) {
      this.totalRows = filteredItems.length;
      this.currentPage = 1;
    },

    getGroupHeader(item) {
      if (!item) return "";
      return `${item.pillar_title} - ${item.pillar_subtitle} | ${
        item.objective_title
      } | ${this.truncateText(item.action_title, 100)}`;
    },

    truncateText(text, maxLength) {
      if (text && text.length > maxLength) {
        return text.substring(0, maxLength) + "...";
      }
      return text;
    },

    async downloadAttachedDocument(id, document_name = "terms_of_reference") {
      if (id) {
        try {
          const response = await axiosIns.get(
            `/workingGroup/downloadAttachedDocument/${id}`,
            {
              responseType: "blob",
            }
          );

          const contentType = response.headers["content-type"];
          const contentDisposition = response.headers["content-disposition"];
          let fileName = document_name ?? "terms_of_reference.pdf";

          if (contentDisposition) {
            const fileNameMatch = contentDisposition.match(/filename="(.+)"/);
            if (fileNameMatch && fileNameMatch.length === 2) {
              fileName = fileNameMatch[1];
            }
          }

          const url = window.URL.createObjectURL(
            new Blob([response.data], { type: contentType })
          );
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", fileName);
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
        } catch (error) {
          console.error("Download failed:", error);
          this.$toast({
            component: ToastificationContent,
            props: {
              title: "Error",
              text: "Failed to download document",
              variant: "danger",
            },
          });
        }
      }
    },

    validateFileType(file) {
      if (!file) return null;
      return (
        file.type === "application/pdf" ||
        file.name.toLowerCase().endsWith(".pdf")
      );
    },

    async showDocumentsModal(item) {
      try {
        const response = await axiosIns.get(
          `/workingGroup/documents/${item.id}`
        );

        this.workingGroupDocuments = {
          document_file: response.data.document_file ?? null,
          meeting_minutes_files: response.data.meeting_minutes_files || [],
        };

        this.$bvModal.show("documents-modal");
      } catch (error) {
        console.error("Error loading documents:", error);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Error",
            text: "Failed to load documents",
            variant: "danger",
          },
        });
      }
    },

    clearWgtoolDate() {
      this.filter_wgtool_date = null;
      this.getWorkingGroupTools();
    },

    formatDateYMD(date) {
      if (!date) return "";
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    },
  },
  watch: {
    selectedAction: {
      handler(newVal) {
        if (newVal) {
          const action = this.workingGroupActions.find(
            (a) => a.action_tool_id === newVal
          );
          if (action) {
            this.subsectionInputsForm = {
              ...this.subsectionInputsForm,
              action_tool_id: action.action_tool_id,
              // pillarText: action.pillar_title ? action.pillar_title : "-",
              // pillarTitleText: action.pillar_subtitle
              //   ? action.pillar_subtitle
              //   : "-",
              // actionText: action.action_title ? action.action_title : "-",
            };
          }
        }
      },
      immediate: true,
    },
  },
};
</script>

<style scoped>
.table-responsive {
  overflow-x: auto;
  max-width: 100%;
  position: relative;
  z-index: 1;
}

.table-responsive.b-table-sticky-header {
  max-height: 75vh !important;
}

.table {
  width: max-content !important;
  min-width: 100% !important;
}

.terms-popover {
  max-width: 400px !important;
}

.terms-popover-content {
  max-height: 300px;
  overflow-y: auto;
  white-space: pre-wrap;
  word-break: break-word;
  padding: 5px;
}

/* Customize scrollbar for the popover content */
.terms-popover-content::-webkit-scrollbar {
  width: 6px;
}

.terms-popover-content::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.terms-popover-content::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 3px;
}

.terms-popover-content::-webkit-scrollbar-thumb:hover {
  background: #555;
}

.group-header {
  background-color: #f8f9fa;
  border-left: 4px solid #007bff;
  margin-bottom: 1rem;
}

.group-header h5 {
  color: #2c3e50;
  margin-bottom: 0.5rem;
}

.group-header p {
  font-size: 0.9rem;
  margin-bottom: 0.25rem;
}

.group-header .text-muted {
  font-size: 0.85rem;
}

.connected-actions-list {
  max-height: 300px;
  overflow-y: auto;
}

.connected-action-item {
  background-color: #f8f9fa;
  margin-bottom: 8px;
  border-radius: 4px;
}

.connected-action-item:last-child {
  margin-bottom: 0;
}

.pillar-title {
  font-size: 0.9rem;
  line-height: 1.4;
}

.action-details {
  color: #6c757d;
  line-height: 1.4;
}

/* Customize scrollbar */
.connected-actions-list::-webkit-scrollbar {
  width: 6px;
}

.connected-actions-list::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.connected-actions-list::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 3px;
}

.connected-actions-list::-webkit-scrollbar-thumb:hover {
  background: #555;
}
</style>

<style>
.vs__dropdown-menu {
  z-index: 9999 !important;
}
.animate__animated {
  animation-fill-mode: none;
}

.truncate-text {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.5;
  max-height: 4.5em; /* 1.5 line-height * 3 lines */
}

.popover-content {
  max-height: 300px;
  overflow-y: auto;
  white-space: pre-wrap;
  word-break: break-word;
  padding: 5px;
}

/* Customize scrollbar for the popover content */
.popover-content::-webkit-scrollbar {
  width: 6px;
}

.popover-content::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.popover-content::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 3px;
}

.popover-content::-webkit-scrollbar-thumb:hover {
  background: #555;
}
</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
@import "@core/scss/vue/libs/vue-flatpicker.scss";
@import "~vue-context/src/sass/vue-context";
</style>

<style scoped>
/* Small v-select styling */
.small-select >>> .vs__dropdown-toggle {
  padding: 2px 4px !important;
  min-height: 28px !important;
  max-height: 28px !important;
  background: #f8fafc !important;
  border: 1px solid #cbd5e0 !important;
  display: flex;
  align-items: center;
}

.small-select >>> .vs__selected {
  font-size: 0.8rem !important;
  margin: 0 !important;
  padding: 0 !important;
  line-height: 24px !important;
}

.shortcut-buttons-flatpickr-button {
  border-radius: 5px;
  background: white;
  border: 1px solid #d8d6de;
}

.small-select >>> .vs__actions {
  padding: 2px 4px !important;
}

.small-select >>> .vs__search::placeholder,
.small-select >>> .vs__dropdown-toggle::placeholder,
.small-select >>> .vs__selected-options .vs__selected {
  font-size: 0.8rem !important;
  line-height: 24px !important;
}

.small-select >>> .vs__search {
  padding: 0 !important;
  margin: 0 !important;
}

.small-select >>> .vs__placeholder {
  font-size: 0.8rem !important;
  line-height: 24px !important;
  padding: 0 !important;
  margin: 0 !important;
  color: #6e6b7b !important;
}

.small-select {
  flex: 1;
  max-width: 120px;
  min-width: 120px;
}
</style>

<style>
.vs__dropdown-option--disabled {
  opacity: 0.6;
  cursor: not-allowed !important;
  background-color: #f8f9fa !important;
}

.top-btns {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
}

.subsections-popover.popover {
  /* Override Bootstrap’s default max-width (around 276px) */
  max-width: 500px !important;
  width: 500px !important;
}

/* If you want it almost full‐screen horizontally: */
.subsections-popover.popover {
  max-width: 60vw !important; /* up to 90% of the viewport width */
  width: 60vw !important;
}
</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>
